import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import YouTube, { YouTubeProps } from "react-youtube";
import Snowfall from "react-snowfall";
import moment from "moment";

import Header from "../../components/header/Header";
import RadioPlayer from "../../components/common/player/radioPlayer/RadioPlayer";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import {
  selectMainState,
  setConfirmationByMessageModal,
  setErrorMessageByVoteModal,
  setFeedbackModal,
  setSuccessMessageByVoteModal,
  setSuccessMessageModal,
  setVoteModal,
} from "../../store/slices/main";
import ScrollUp from "../../components/scrollUp/ScrollUp";
import HlsPlayer from "../../components/common/player/hlsPlayer/HlsPlayer";
import Footer from "../../components/footer/Footer";
import { getHealthCheck } from "../../api/asyncThuncks/healthCheck";
import { HealthStatusEnum } from "../../common/enums";
import { mainRoutes } from "../../routes/routes";
import {
  getAddsSliderImgs,
  getHomePageSetting,
  getPreRoll,
} from "../../api/asyncThuncks/settings";
import { newsCards } from "../../api/asyncThuncks/newsCards";
import { selectNews, setYoutubeUrlState } from "../../store/slices/news";
import Modal from "../../components/modal/Modal";
import FeedbackModal from "../../components/forms/FeedbackModal";
import SuccessMessageModal from "../../components/forms/SuccessMessageModal";
import VoteFormModal from "../../components/forms/VoteFormModal";
import ConfirmationByMessageModal from "../../components/forms/VoteFormModal/components/ConfirmationByMessageModal";
import SuccessMessageByVoteModal from "../../components/forms/VoteFormModal/components/SuccessMessageByVoteModal";
import ErrorMessageByVoteModal from "../../components/forms/VoteFormModal/components/ErrorMessageByVoteModal";
const loadingGif = require("../../assets/rs_loader.gif") as string;

const MainLayout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    feedbackModal,
    successMessageModal,
    voteModal,
    confirmationByMessageModal,
    successMessageByVoteModal,
    errorMessageByVoteModal,
  } = useAppSelector(selectMainState);
  const [showSplash, setShowSplash] = useState(true);
  const [isYoutubeActive, setIsYoutubeActive] = useState(false);
  const [playerWidth, setPlayerWidth] = useState("");
  const [playerHeight, setPlayerHeight] = useState("");
  const {
    radioPlayer: { radio },
    audioPlayer: { playForTrack },
    healthCheck,
  } = useAppSelector(selectMainState);

  const { youtubeUrl } = useAppSelector(selectNews);
  const youtubeLinkId = youtubeUrl.split("/")[3];
  const currentMonth = moment().month();
  const isWinter =
    currentMonth === 11 || currentMonth === 0 || currentMonth === 1;

  const onPlayerReady: YouTubeProps["onReady"] = (event) => {
    event.target.pauseVideo();
    setIsYoutubeActive(true);
  };

  const getYouTubePlayerSize = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    let width, height;

    if (screenWidth < 720) {
      width = "320px";
      height = "180px";
    } else if (screenWidth >= 720 && screenWidth < 1024) {
      width = "640px";
      height = "360px";
    } else {
      width = "1024px";
      height = "576px";
    }

    // 4320p (8K): 7680 x 4320
    // 2160p (4K): 3840 x 2160
    // 1440p (2K): 2560 x 1440
    // 1080p (HD): 1920 x 1080
    // 720p (HD): 1280 x 720
    // 480p (SD): 854 x 480
    // 360p (SD): 640 x 360
    // 240p (SD): 426 x 240

    return { width, height };
  };

  const handeleClick = () => {
    dispatch(setYoutubeUrlState(""));
    setIsYoutubeActive(false);
  };

  useEffect(() => {
    dispatch(getHealthCheck());
  }, []);

  useEffect(() => {
    if (healthCheck === HealthStatusEnum.Fail) {
      navigate(mainRoutes.notFound.path);
    }

    if (healthCheck === HealthStatusEnum.Pass) {
      const currentPath = window.location.pathname;
      navigate(currentPath);
    }
  }, [healthCheck]);

  useEffect(() => {
    dispatch(getAddsSliderImgs());
    dispatch(getHomePageSetting());
    dispatch(newsCards());
    dispatch(getPreRoll());

    const splashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);

    return () => {
      clearTimeout(splashTimer);
    };
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") handeleClick();
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (isYoutubeActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isYoutubeActive]);

  useEffect(() => {
    const { width, height } = getYouTubePlayerSize();
    setPlayerWidth(`${width}`);
    setPlayerHeight(`${height}`);
  }, []);

  return (
    <>
      {showSplash ? (
        <div>
          <div className="min-w-screen min-h-screen flex justify-center items-center bg-white z-50">
            <img
              className="max-w-[200px] max-h-[200px]"
              src={loadingGif}
              alt="loading..."
            />
            {/* <Splash /> */}
          </div>
        </div>
      ) : (
        <div>
          {isWinter && (
            <div className="absolute top-0 left-0 w-full h-full z-[1000] pointer-events-none">
              <Snowfall snowflakeCount={100} />
            </div>
          )}
          <div
            className={`relative custom-background-image flex flex-col justify-center items-center w-full h-full sm:bg-white overflow-x-hidden bg-white`}
          >
            <Header />
            <div className="relative w-screen max-w-[1000px] min-h-[calc(100vh_-_10rem)] flex flex-col">
              <div className="flex-grow">
                <Outlet />
                <div
                  className={
                    radio
                      ? "w-full flex justify-center max-w-md md:max-w-lg px-2.5"
                      : "hidden"
                  }
                >
                  <RadioPlayer />
                </div>
                <div
                  className={
                    playForTrack
                      ? "w-full flex justify-center max-w-md md:max-w-lg px-2.5"
                      : "hidden"
                  }
                >
                  <div
                    className={
                      playForTrack
                        ? "fixed w-full bottom-0 left-0 z-40 min-h-[4rem]"
                        : "min-h-[4rem] px-2"
                    }
                  >
                    <HlsPlayer />
                  </div>
                </div>
                <ScrollUp />
              </div>
              <div
                className={`w-full mt-auto ${
                  !!radio ? "mb-16 md:mb-14" : "mb-0"
                }`}
              >
                <Footer />
              </div>
            </div>

            {youtubeUrl && (
              <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50">
                <div
                  className={`relative w-full w-[${playerWidth}] h-[${playerHeight}] z-50`}
                >
                  <YouTube
                    videoId={youtubeLinkId.split("=")[1]}
                    opts={{
                      height: playerHeight,
                      width: playerWidth,
                      playerVars: { autoplay: 0 },
                    }}
                    onReady={onPlayerReady}
                    className="absolute flex justify-center items-center inset-0 w-full h-full shadow-2xl"
                  />
                </div>
                <div
                  onClick={handeleClick}
                  className="absolute inset-0 bg-black opacity-50 z-20"
                ></div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="modal">
        <Modal
          active={feedbackModal}
          onClick={() => dispatch(setFeedbackModal(false))}
        >
          <FeedbackModal />
        </Modal>

        <Modal
          active={successMessageModal}
          onClick={() => dispatch(setSuccessMessageModal(false))}
        >
          <SuccessMessageModal />
        </Modal>

        <Modal active={voteModal} onClick={() => dispatch(setVoteModal(false))}>
          <VoteFormModal />
        </Modal>

        <Modal
          active={confirmationByMessageModal}
          onClick={() => dispatch(setConfirmationByMessageModal(false))}
        >
          <ConfirmationByMessageModal />
        </Modal>

        <Modal
          active={successMessageByVoteModal}
          onClick={() => dispatch(setSuccessMessageByVoteModal(false))}
        >
          <SuccessMessageByVoteModal />
        </Modal>

        <Modal
          active={errorMessageByVoteModal}
          onClick={() => dispatch(setErrorMessageByVoteModal(false))}
        >
          <ErrorMessageByVoteModal />
        </Modal>
      </div>
    </>
  );
};

export default MainLayout;
